/**
 * The session ↔ protocol pin (thin H3 / §6 d4).
 *
 * A session pins ONE protocol template version plus a physio-selected
 * week/day — chosen by the physio and stored as data, never inferred and
 * never defaulted silently. No selection on record = no slot, and the target
 * display stays honestly empty. When the A4 calendar lands it auto-fills the
 * SAME fields with provenance 'system'; nothing here is thrown away.
 *
 * This module fixes only the SHAPE. Template content — the codes, targets,
 * and calendars a pin resolves to — is clinical content and lives in
 * @advaitnetra/protocol (§6 d5: this package stays clinical-content-free).
 * Semantic bounds (does week 9 exist in this template?) are checked at the
 * API against the resolved template.
 */

import { z } from 'zod';
import { EVENT_PROVENANCES } from './taps';

/**
 * "TEMPLATE-ID@major.minor", e.g. "AN-TKR-STD@0.1" — one string so a pin can
 * never name a template without a version or a version without a template.
 */
export const protocolVersionPinSchema = z
  .string()
  .regex(/^[A-Z][A-Z0-9-]{1,31}@\d+\.\d+$/, 'malformed protocol version pin');
export type ProtocolVersionPin = z.infer<typeof protocolVersionPinSchema>;

/** POST /api/sessions/:sessionId/protocol — physio sets (or corrects) the pin. */
export const setProtocolSelectionRequestSchema = z.object({
  protocolVersion: protocolVersionPinSchema,
  /** Syntactic caps only; the template's own bounds are enforced at the API. */
  week: z.number().int().min(1).max(52),
  dayOfWeek: z.number().int().min(1).max(7),
  /** Client event time (ADR-0010 §3); defaults to server time when omitted. */
  occurredAt: z.iso.datetime().optional(),
});
export type SetProtocolSelectionRequest = z.infer<typeof setProtocolSelectionRequestSchema>;

/** The current selection, resolved from the append-only event series at read time. */
export const protocolSelectionSchema = z.object({
  protocolVersion: protocolVersionPinSchema,
  week: z.number().int().min(1),
  dayOfWeek: z.number().int().min(1),
  occurredAt: z.iso.datetime(),
  recordedAt: z.iso.datetime(),
  actor: z.string().min(1),
  provenance: z.enum(EVENT_PROVENANCES),
});
export type ProtocolSelection = z.infer<typeof protocolSelectionSchema>;

export const setProtocolSelectionResponseSchema = z.object({
  ok: z.literal(true),
  selection: protocolSelectionSchema,
});
export type SetProtocolSelectionResponse = z.infer<typeof setProtocolSelectionResponseSchema>;

/** GET — `selection` is null until a physio has set one (§6 d4: never defaulted). */
export const getProtocolSelectionResponseSchema = z.object({
  ok: z.literal(true),
  selection: protocolSelectionSchema.nullable(),
});
export type GetProtocolSelectionResponse = z.infer<typeof getProtocolSelectionResponseSchema>;
